import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Hint, Inscription, PlusIcon, Text, useResponsive } from '@common-fe/common-fe';
import { toNumber } from 'lodash';

import { useStore } from '@/modules/transaction/components/ReimburseMe/store/usePaymentMethods.store';
import { useManageProfileModalsStore } from '@/modules/user/components/ManageProfile/stores/useManageProfileModals.store';

import EmployeePaymentMethodsList from './EmployeePaymentMethodsList';

interface Props {
  refetchPaymentMethods: () => void;
  hasAllowedPaymentMethods?: boolean;
}

const EmployeePaymentMethods: React.FC<Props> = ({ refetchPaymentMethods, hasAllowedPaymentMethods }) => {
  const { isMobile } = useResponsive();
  const { t } = useTranslation();
  const paymentMethods = useStore((state) => state.methods);
  const { handleSetIsAddPaymentMethodModalVisible } = useManageProfileModalsStore();

  const handleOpenAddPaymentMethodModal = useCallback(() => {
    handleSetIsAddPaymentMethodModalVisible(true);
  }, [handleSetIsAddPaymentMethodModalVisible]);

  const sortedPaymentMethods = useMemo(() => {
    if (!paymentMethods?.length) {
      return [];
    }

    return [...paymentMethods].sort((prev, next) => {
      if (prev.isDefault && !next.isDefault) {
        return -1;
      } else if (!prev.isDefault && next.isDefault) {
        return 1;
      } else {
        return toNumber(prev.id) - toNumber(next.id);
      }
    });
  }, [paymentMethods]);

  if (!sortedPaymentMethods?.length) {
    return null;
  }

  return (
    <Box
      pad="spacing16"
      background="canvas"
      round="container1Round"
      height={isMobile ? { min: '0px' } : { min: '226px' }}
      width={isMobile ? { width: '100%' } : { max: '275px', width: '100%' }}
      margin={isMobile ? { top: 'spacing16' } : {}}
    >
      <Inscription color="textSecondary" weight={500} size="small">
        {t('How Should We Reimburse You?')}
      </Inscription>

      <Box margin={{ top: 'spacing4' }}>
        <EmployeePaymentMethodsList
          paymentMethodsList={sortedPaymentMethods}
          refetchPaymentMethods={refetchPaymentMethods}
        />
      </Box>

      <Box margin={{ top: 'spacing12' }} height={{ min: 'auto' }}>
        <Hint
          hintAlign="vertical"
          hintWidth={200}
          hideHint={hasAllowedPaymentMethods}
          marginContent="m"
          hintElement={
            <Box
              direction="row"
              align="center"
              {...(hasAllowedPaymentMethods ? { onClick: handleOpenAddPaymentMethodModal } : {})}
            >
              <PlusIcon
                size="16px"
                color={hasAllowedPaymentMethods ? 'iconAccent' : 'textDisabled'}
                style={{ padding: '4px' }}
              />
              <Inscription
                margin={{ left: 'spacing8' }}
                color={hasAllowedPaymentMethods ? 'iconAccent' : 'textDisabled'}
              >
                {t('Add Payment Method')}
              </Inscription>
            </Box>
          }
        >
          <Box direction="row" justify="center" align="center">
            <Text size="small" textAlign="center">
              {t('There are no more available payment methods.')}
            </Text>
          </Box>
        </Hint>
      </Box>
    </Box>
  );
};

export default EmployeePaymentMethods;
